import { createFileRoute } from '@tanstack/react-router';
import {
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    Radio,
    Tags,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    FilterBar,
    type FilterDimension,
} from '@apps/web/components/ui/filter_bar';
import {
    AdminButton,
    AdminInput,
    AdminSearchInput,
} from '@apps/web/features/admin_design/admin_controls';
import {
    AdminPage,
    AdminPageBody,
    AdminPageHeader,
} from '@apps/web/features/admin_design/admin_shell';
import { AdminTableSkeleton } from '@apps/web/features/admin_design/admin_skeleton';
import {
    AdminEmptyState,
    AdminErrorState,
} from '@apps/web/features/admin_design/admin_states';
import {
    CALL_TABLE_COLUMNS,
    CallsTable,
} from '@apps/web/routes/_app/admin/calls/calls_table';
import {
    ANALYSIS_CALL_TYPE_LABELS,
    ANALYSIS_CALL_TYPE_VALUES,
    ANALYZED_FILTER_LABELS,
    ANALYZED_FILTER_VALUES,
    CALL_SOURCE_LABELS,
    CALL_SOURCE_VALUES,
    type CallFilterKey,
    type CallsSearch,
    callsSearchSchema,
    filterChipsToAnalyzed,
    filterChipsToSource,
    filterChipsToType,
    PER_PAGE,
    searchToFilterChips,
} from '@apps/web/routes/_app/admin/calls/search_params';
import { useAdminCalls } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/admin/calls/')({
    component: CallsPage,
    head: () => ({ meta: [{ title: 'Calls' }] }),
    validateSearch: callsSearchSchema,
});

const PARTICIPANT_DEBOUNCE_MS = 300;

// Mirror Core's `normalizeTagSlug` so a typed label like "Pricing" or
// "COMPETITOR-mention" matches the lowercase slug stored on the tag. Core
// matches the `tag` filter by exact slug, so the client must slugify too.
function toTagSlug(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/['"]/gu, '')
        .replace(/[^a-z0-9]+/gu, '-')
        .replace(/^-+|-+$/gu, '');
}

const SOURCE_DIMENSION: FilterDimension<CallFilterKey> = {
    key: 'source',
    label: 'Source',
    operatorLabel: 'is',
    icon: Radio,
    multiSelect: false,
    options: CALL_SOURCE_VALUES.map((value) => ({
        value,
        label: CALL_SOURCE_LABELS[value],
    })),
};

const TYPE_DIMENSION: FilterDimension<CallFilterKey> = {
    key: 'type',
    label: 'Type',
    operatorLabel: 'is',
    icon: Tags,
    multiSelect: false,
    options: ANALYSIS_CALL_TYPE_VALUES.map((value) => ({
        value,
        label: ANALYSIS_CALL_TYPE_LABELS[value],
    })),
};

const ANALYZED_DIMENSION: FilterDimension<CallFilterKey> = {
    key: 'analyzed',
    label: 'Analysis',
    operatorLabel: 'is',
    icon: CheckCircle2,
    multiSelect: false,
    options: ANALYZED_FILTER_VALUES.map((value) => ({
        value,
        label: ANALYZED_FILTER_LABELS[value],
    })),
};

function CallsPage() {
    const navigate = Route.useNavigate();
    const search = Route.useSearch();
    const { page, source, type, analyzed, q } = search;

    const updateSearch = useCallback(
        (updates: Partial<CallsSearch>) => {
            navigate({
                search: (current) => ({ ...current, ...updates }),
            });
        },
        [navigate],
    );

    // Search box: a local draft committed to the URL only on Enter, so each
    // keystroke does not trigger a vector search. Re-sync when the URL `q`
    // changes externally (back/forward, shared links).
    const [searchDraft, setSearchDraft] = useState(q ?? '');
    useEffect(() => {
        setSearchDraft(q ?? '');
    }, [q]);

    // Participant filter: a local draft for responsive typing. Only a user
    // keystroke schedules a debounced commit to the URL — the URL→draft sync
    // below never commits, so back/forward navigation cannot be reverted by a
    // stale debounced value. A pending commit is cleared on unmount.
    const [participantDraft, setParticipantDraft] = useState(
        search.participant ?? '',
    );
    useEffect(() => {
        setParticipantDraft(search.participant ?? '');
    }, [search.participant]);
    const participantCommitTimer = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    useEffect(() => {
        return () => {
            if (participantCommitTimer.current) {
                clearTimeout(participantCommitTimer.current);
            }
        };
    }, []);
    const handleParticipantChange = (value: string) => {
        setParticipantDraft(value);
        if (participantCommitTimer.current) {
            clearTimeout(participantCommitTimer.current);
        }
        participantCommitTimer.current = setTimeout(() => {
            updateSearch({ participant: value.trim() || undefined, page: 1 });
        }, PARTICIPANT_DEBOUNCE_MS);
    };

    // Tag filter: same local-draft + debounced commit pattern as participant.
    const [tagDraft, setTagDraft] = useState(search.tag ?? '');
    useEffect(() => {
        setTagDraft(search.tag ?? '');
    }, [search.tag]);
    const tagCommitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        return () => {
            if (tagCommitTimer.current) {
                clearTimeout(tagCommitTimer.current);
            }
        };
    }, []);
    const handleTagChange = (value: string) => {
        setTagDraft(value);
        if (tagCommitTimer.current) {
            clearTimeout(tagCommitTimer.current);
        }
        tagCommitTimer.current = setTimeout(() => {
            updateSearch({ tag: toTagSlug(value) || undefined, page: 1 });
        }, PARTICIPANT_DEBOUNCE_MS);
    };

    const { data, isLoading, error } = useAdminCalls({
        q: q || undefined,
        source_kind: source,
        participant: search.participant || undefined,
        tag: search.tag || undefined,
        analysis_call_type: type,
        analyzed: analyzed === undefined ? undefined : analyzed === 'analyzed',
        // Native date inputs yield `YYYY-MM-DD`; treat the range as inclusive of
        // the whole UTC day so a same-day "on or before" does not drop calls.
        started_after: dayStartIso(search.started_after),
        started_before: dayEndIso(search.started_before),
        order: search.order,
        page,
        per_page: PER_PAGE,
    });

    const calls = useMemo(() => data?.items ?? [], [data?.items]);
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

    // A stale or shared URL can point past the last page; clamp it so the user
    // is not stranded on an empty table.
    useEffect(() => {
        if (!isLoading && page > totalPages) {
            navigate({
                search: (current) => ({ ...current, page: totalPages }),
                replace: true,
            });
        }
    }, [isLoading, navigate, page, totalPages]);

    const filterChips = searchToFilterChips(source, type, analyzed);

    const submitSearch = () => {
        updateSearch({ q: searchDraft.trim() || undefined, page: 1 });
    };

    return (
        <AdminPage>
            <AdminPageHeader
                title="Calls"
                meta={error ? undefined : `${total.toLocaleString()} indexed`}
            >
                <div className="flex flex-wrap items-center gap-2">
                    <AdminSearchInput
                        value={searchDraft}
                        onChange={(event) => setSearchDraft(event.target.value)}
                        onClear={() => setSearchDraft('')}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                submitSearch();
                            }
                        }}
                        placeholder="Search transcripts…"
                        containerClassName="w-64"
                    />
                    <FilterBar
                        dimensions={[
                            SOURCE_DIMENSION,
                            TYPE_DIMENSION,
                            ANALYZED_DIMENSION,
                        ]}
                        chips={filterChips}
                        onChange={(chips) =>
                            updateSearch({
                                source: filterChipsToSource(chips),
                                type: filterChipsToType(chips),
                                analyzed: filterChipsToAnalyzed(chips),
                                page: 1,
                            })
                        }
                    />
                    <AdminInput
                        value={participantDraft}
                        onChange={(event) =>
                            handleParticipantChange(event.target.value)
                        }
                        placeholder="Participant or domain"
                        className="w-48"
                    />
                    <AdminInput
                        value={tagDraft}
                        onChange={(event) =>
                            handleTagChange(event.target.value)
                        }
                        placeholder="Tag slug"
                        className="w-40"
                        aria-label="Filter by tag slug"
                    />
                    <div className="flex items-center gap-1">
                        <AdminInput
                            type="date"
                            value={search.started_after ?? ''}
                            onChange={(event) =>
                                updateSearch({
                                    started_after:
                                        event.target.value || undefined,
                                    page: 1,
                                })
                            }
                            className="w-36"
                            aria-label="Calls on or after"
                        />
                        <span className="text-admin-xs text-admin-ink-muted">
                            to
                        </span>
                        <AdminInput
                            type="date"
                            value={search.started_before ?? ''}
                            onChange={(event) =>
                                updateSearch({
                                    started_before:
                                        event.target.value || undefined,
                                    page: 1,
                                })
                            }
                            className="w-36"
                            aria-label="Calls on or before"
                        />
                    </div>
                </div>
            </AdminPageHeader>
            <AdminPageBody>
                <div className="text-admin-sm text-admin-ink-muted flex items-center justify-between px-6 py-3">
                    <span className="tabular-nums">
                        Page {page} of {totalPages}
                    </span>
                    <div className="flex gap-2">
                        <AdminButton
                            variant="secondary"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => updateSearch({ page: page - 1 })}
                        >
                            <ChevronLeft strokeWidth={1.75} />
                            Previous
                        </AdminButton>
                        <AdminButton
                            variant="secondary"
                            size="sm"
                            disabled={page >= totalPages}
                            onClick={() => updateSearch({ page: page + 1 })}
                        >
                            Next
                            <ChevronRight strokeWidth={1.75} />
                        </AdminButton>
                    </div>
                </div>
                {isLoading ? (
                    <AdminTableSkeleton columns={CALL_TABLE_COLUMNS} />
                ) : error ? (
                    <AdminErrorState
                        title="Failed to load calls"
                        description="Please try again."
                    />
                ) : calls.length === 0 ? (
                    <AdminEmptyState
                        icon={Radio}
                        title="No calls found"
                        description="Try different filters or clear the search."
                    />
                ) : (
                    <CallsTable calls={calls} />
                )}
            </AdminPageBody>
        </AdminPage>
    );
}

// A native date input yields `YYYY-MM-DD` as a calendar date in the viewer's
// local timezone. Parse it as a local instant (no trailing `Z`) and convert to
// UTC, so the range covers the viewer's whole local day inclusive on both ends.
function dayStartIso(date: string | undefined): string | undefined {
    return date ? new Date(`${date}T00:00:00.000`).toISOString() : undefined;
}

function dayEndIso(date: string | undefined): string | undefined {
    return date ? new Date(`${date}T23:59:59.999`).toISOString() : undefined;
}
